import { format as dateFnsFormat, parseISO } from 'date-fns';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInHours from 'date-fns/differenceInHours';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import differenceInMonths from 'date-fns/differenceInMonths';
import differenceInWeeks from 'date-fns/differenceInWeeks';
import differenceInYears from 'date-fns/differenceInYears';
import endOfWeek from 'date-fns/endOfWeek';
import format from 'date-fns/format';
import isToday from 'date-fns/isToday';
import isWithinInterval from 'date-fns/isWithinInterval';
import isYesterday from 'date-fns/isYesterday';
import startOfWeek from 'date-fns/startOfWeek';
import subWeeks from 'date-fns/subWeeks';

const FORMAT_DAY = "MM'/'dd'/'yy";
const FORMAT_TIME = 'h:mmaaa';
/**
 *
 * @param {string | Date} date - a date we should format, either as a string or a Date object
 *
 * @returns {string} - returns the formatted date as a string in the format of MM/DD/YY hh:mm:ss aaa
 *
 * @example const formatted = formatDate('2020-12-01T11:34:56.789Z') => '12/01/20 11:34 am'
 * @example const formatted = formatDate(new Date(2020, 11, 01, 11, 34, 56)) => '12/01/20 11:34 am'
 */
export const formatDate = (date: string | Date | undefined, format?: string): string =>
  date ? dateFnsFormat(date instanceof Date ? date : parseISO(date), format || FORMAT_DAY) : '';

export const formatTime = (date: string | Date | undefined, format?: string): string =>
  date ? dateFnsFormat(date instanceof Date ? date : parseISO(date), format || FORMAT_TIME) : '';

export function formatTimeToReadable(date: Date): string {
  const now = new Date();
  const diffInMinutes = differenceInMinutes(now, date);

  if (diffInMinutes < 1) {
    return 'Just now';
  }

  if (diffInMinutes < 60) {
    return `${diffInMinutes} min${diffInMinutes !== 1 ? 's' : ''} ago`;
  }

  const diffInHours = differenceInHours(now, date);
  if (diffInHours < 24 && isToday(date)) {
    return `Today, ${format(date, 'h:mm a')}`;
  }

  if (diffInHours < 48 && isYesterday(date)) {
    return `Yesterday, ${format(date, 'h:mm a')}`;
  }

  const diffInDays = differenceInDays(now, date);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  }

  // Check if it's last week
  const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 0 });
  const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 0 });

  if (isWithinInterval(date, { start: lastWeekStart, end: lastWeekEnd })) {
    return 'Last week';
  }

  const diffInWeeks = differenceInWeeks(now, date);
  if (diffInWeeks < 5) {
    // Up to 4 weeks ago
    return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
  }

  const diffInMonths = differenceInMonths(now, date);
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
  }

  const diffInYears = differenceInYears(now, date);
  return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
}
